import dayjs from 'dayjs';
import { api, type RouterInputs, type RouterOutputs, skipBatchOption } from '~/utils/api';

import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { FilterTriggerIcon } from '~/components/FilterMenu';
import { capitalCase } from 'change-case';
import { UNKNOWN_BRAND_ID } from '~/config';
import useScanHistoryFilter from './useScanHistoryFilter';
import Pagination, { usePagination } from '~/components/Pagination';
import Chip from '~/components/Chip';
import { SortableTableHeader } from '~/components/TableSortControl';
import Skeleton from '~/components/Loading/Skeleton';
import AppSpinner from '~/components/Loading/AppSpinner';
import Report from './Report';
import useQueryString from '~/hooks/useQueryString';
import ReportActions from './ReportActions';
import Button from '~/components/Button';
import Dropdown from '~/components/Dropdown';
import usePopover from '~/hooks/usePopover';
import { cn } from '~/lib/utils';
import { useMemo, useRef, useState } from 'react';

export type Filters = { brandIds: string[] };

type TScanHistory = RouterOutputs['scanner']['getScanHistory']['reports'];
type ScanHistoryQueryInput = RouterInputs['scanner']['getScanHistory'];
type ScanHistoryCache = RouterOutputs['scanner']['getScanHistory'];

const isSameScanHistoryQuery = (a: ScanHistoryQueryInput, b: ScanHistoryQueryInput) =>
  JSON.stringify(a) === JSON.stringify(b);

/** Restore the delete-time cache, and put the row back on the query the UI is showing. */
export function restoreScanHistoryDeleteSnapshot({
  utils,
  snapshot,
  currentQueryKey,
  reportId,
}: {
  utils: ReturnType<typeof api.useUtils>;
  snapshot: { queryKey: ScanHistoryQueryInput; data: ScanHistoryCache };
  currentQueryKey: ScanHistoryQueryInput;
  reportId: string;
}) {
  utils.scanner.getScanHistory.setData(snapshot.queryKey, snapshot.data);

  if (isSameScanHistoryQuery(snapshot.queryKey, currentQueryKey)) {
    return;
  }

  const restored = snapshot.data.reports.find((report) => report.id === reportId);
  if (!restored) return;

  utils.scanner.getScanHistory.setData(currentQueryKey, (prev) => {
    if (!prev || prev.reports.some((report) => report.id === reportId)) {
      return prev;
    }
    return {
      ...prev,
      reports: [restored, ...prev.reports],
      totalCount: prev.totalCount + 1,
    };
  });
}

type FeedbackCountKey = 'claims' | 'policyFeedback' | 'trademarkInstances';

type LegacyHistoryRecord = {
  ScannerRunDocuments: Array<{
    createdAt: Date;
    reviewedByUserId: string | null;
    Document: {
      id: string;
      ExistingDocument?: {
        _count: Record<FeedbackCountKey, number>;
      } | null;
      _count: Record<FeedbackCountKey, number>;
    };
  }>;
};

export type FormattedHistory = Array<{
  name: string;
  id: string;
  brandId: string;
  brandName: string;
  claimCount: number;
  policyFeedbackCount: number;
  trademarkCount: number;
  documentIds: string[];
  lastScanAt: string;
  isReviewed: boolean;
}>;

const countFeedback = (historyRecord: LegacyHistoryRecord, feedbackType: FeedbackCountKey) => {
  const docIds = new Set<string>();
  return historyRecord.ScannerRunDocuments.reduce((acc, curr) => {
    if (docIds.has(curr.Document.id)) return acc;

    docIds.add(curr.Document.id);
    return (
      acc +
      (curr.Document.ExistingDocument
        ? curr.Document.ExistingDocument._count[feedbackType]
        : curr.Document._count[feedbackType])
    );
  }, 0);
};

const hasLegacyScanDocuments = (record: TScanHistory[0]): record is TScanHistory[0] & LegacyHistoryRecord =>
  'ScannerRunDocuments' in record;

export const getIsReportReviewed = (
  report: Pick<TScanHistory[0], 'reviewedByUserId'> & {
    ScannerRunDocuments?: Array<{ reviewedByUserId: string | null }>;
    stats?: {
      allDocumentsReviewed: boolean;
    };
  },
) =>
  !!report.reviewedByUserId ||
  !!report.stats?.allDocumentsReviewed ||
  (!!report.ScannerRunDocuments?.length && report.ScannerRunDocuments.every((i) => i.reviewedByUserId));

export const formatScanHistory = (scanHistory: TScanHistory): FormattedHistory => {
  return scanHistory.reduce<FormattedHistory>((acc, curr) => {
    if ('stats' in curr && curr.stats) {
      acc.push({
        id: curr.id,
        brandId: curr.brandId || UNKNOWN_BRAND_ID,
        name: curr.name,
        brandName: curr.brand?.name || UNKNOWN_BRAND_ID,
        claimCount: curr.stats.claimCount,
        policyFeedbackCount: curr.stats.policyFeedbackCount,
        trademarkCount: curr.stats.trademarkCount,
        documentIds: curr.stats.documentIds,
        lastScanAt: curr.stats.lastScanAt ? dayjs(curr.stats.lastScanAt).format('MMM DD, YYYY') : '',
        isReviewed: getIsReportReviewed(curr),
      });
      return acc;
    }

    if (!hasLegacyScanDocuments(curr)) {
      return acc;
    }

    acc.push({
      id: curr.id,
      brandId: curr.brandId || UNKNOWN_BRAND_ID,
      name: curr.name,
      brandName: curr.brand?.name || UNKNOWN_BRAND_ID,
      claimCount: countFeedback(curr, 'claims'),
      policyFeedbackCount: countFeedback(curr, 'policyFeedback'),
      trademarkCount: countFeedback(curr, 'trademarkInstances'),
      documentIds: Array.from(new Set(curr.ScannerRunDocuments.map((i) => i.Document.id))),
      lastScanAt: dayjs(curr.ScannerRunDocuments[0]?.createdAt).format('MMM DD, YYYY'),
      isReviewed: getIsReportReviewed(curr),
    });
    return acc;
  }, []);
};

const itemsPerPage = 30;



export default function ScanHistory() {
  const { data: listBrands } = api.scanner.listBrands.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const { brandIds, onFilterChange, onSort, orderBy, clearFilter } = useScanHistoryFilter();

  const { currentPage, setCurrentPage, skip } = usePagination(itemsPerPage);

  const orderByKey = useMemo(() => {
    if (orderBy?.key === 'brandName') return 'brand' as const;
    if (orderBy?.key === 'newFeedback') return 'newFeedback' as const;
    // Default and date-like UI keys (lastScanAt, name, createdAt) sort by report createdAt.
    return 'createdAt' as const;
  }, [orderBy?.key]);

  const { searchParams, updateSearchParams } = useQueryString();
  const queryParamReportId = searchParams?.get('reportId');

  const { open, handleClose, handleOpen, anchorEl } = usePopover<HTMLButtonElement>();

  const queryParams = useMemo(
    () => ({
      skip,
      limit: itemsPerPage,
      filters: {
        brandIds,
      },
      orderBy:
        orderByKey && orderBy?.order
          ? {
              key: orderByKey,
              order: orderBy.order.toLowerCase() as 'asc' | 'desc',
            }
          : undefined,
    }),
    [skip, brandIds, orderByKey, orderBy?.order],
  );

  const utils = api.useUtils();
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set());
  const deleteSnapshotsRef = useRef<
    Map<string, { queryKey: ScanHistoryQueryInput; data: ScanHistoryCache }>
  >(new Map());

  const { data: scanHistory, isLoading, isFetching } = api.scanner.getScanHistory.useQuery(
    queryParams,
    {
      ...skipBatchOption,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Retain the current page's rows while a new page/sort/filter loads — never
      // flash skeletons over data already on screen.
      keepPreviousData: true,
    },
  );

  // Prefetch the next page on pagination hover/focus intent (instead of eagerly on
  // every load), so clicking "Next page" is instant without a wasted 30-row query.
  const nextPageParams = useMemo(
    () => ({
      ...queryParams,
      skip: skip + itemsPerPage,
    }),
    [queryParams, skip],
  );

  const hasNextPage = (scanHistory?.totalCount || 0) > skip + itemsPerPage;

  const prefetchNextPage = () => {
    if (!hasNextPage) return;
    void utils.scanner.getScanHistory.prefetch(nextPageParams, {
      ...skipBatchOption,
      staleTime: 5 * 60 * 1000,
    });
  };

  const rawReports = scanHistory?.reports || [];
  const reports = useMemo(
    () => rawReports.filter((report) => !pendingDeleteIds.has(report.id)),
    [rawReports, pendingDeleteIds],
  );
  const pendingStillInCache = rawReports.filter((report) => pendingDeleteIds.has(report.id)).length;
  const total = Math.max(0, (scanHistory?.totalCount || 0) - pendingStillInCache);
  let totalUniqueBrands = 0;

  if (scanHistory?.totalUniqueBrands[0]) {
    totalUniqueBrands = scanHistory.totalUniqueBrands[0].count || 0;
  }

  const rows = useMemo(() => formatScanHistory(reports), [reports]);

  const currentReport = rows.find((i) => i.id === queryParamReportId);

  const handlePendingDelete = (reportId: string) => {
    const queryKey = queryParams;
    const previous = utils.scanner.getScanHistory.getData(queryKey);
    if (previous) {
      deleteSnapshotsRef.current.set(reportId, { queryKey, data: previous });
    }
    setPendingDeleteIds((prev) => new Set(prev).add(reportId));
    utils.scanner.getScanHistory.setData(queryKey, (prev) =>
      prev
        ? {
            ...prev,
            reports: prev.reports.filter((r) => r.id !== reportId),
            totalCount: Math.max(0, prev.totalCount - 1),
          }
        : prev,
    );
  };

  const handleUndoDelete = (reportId: string) => {
    const snapshot = deleteSnapshotsRef.current.get(reportId);
    deleteSnapshotsRef.current.delete(reportId);
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(reportId);
      return next;
    });
    if (snapshot) {
      restoreScanHistoryDeleteSnapshot({
        utils,
        snapshot,
        currentQueryKey: queryParams,
        reportId,
      });
    }
  };

  const handleDeleteCommitted = (reportId: string) => {
    deleteSnapshotsRef.current.delete(reportId);
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(reportId);
      return next;
    });
    if (queryParamReportId === reportId) {
      updateSearchParams('reportId', null);
    }
  };

  const brands = [
    { id: UNKNOWN_BRAND_ID, name: capitalCase(UNKNOWN_BRAND_ID), isEnabled: true },
    ...(listBrands || []),
  ];

  const filtersNumber = useMemo(() => {
    return brandIds.length > 0 ? 1 : 0;
  }, [brandIds]);

  const showBrandColumn = totalUniqueBrands > 0;
  const brandSortOrder = orderBy?.key === 'brandName' ? orderBy.order : null;

  const toggleBrandSort = () => {
    if (brandSortOrder === 'ASC') {
      onSort('brandName', 'DESC');
      return;
    }
    onSort('brandName', 'ASC');
  };

  const openReport = (reportId: string) => {
    updateSearchParams('reportId', reportId);
  };

  return (
    <section aria-labelledby="scan-history-heading" className="min-w-0" data-testid="scan-history">
      <div className="mb-2 flex w-full items-baseline justify-between gap-3">
        <h2
          id="scan-history-heading"
          className="text-foreground text-sm font-semibold tracking-tight"
        >
          Scan History
        </h2>
        <div className="flex shrink-0 items-center gap-1.5">
          {brands.length > 1 && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={filtersNumber > 0 ? `Filters, ${filtersNumber} selected` : 'Filters'}
                icon={<FilterTriggerIcon active={filtersNumber > 0} />}
                label="Filters"
                onClick={(e) => {
                  if (open) {
                    handleClose();
                    return;
                  }
                  handleOpen(e);
                }}
              />
              <Dropdown
                open={open}
                onClose={handleClose}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                disablePortal={false}
                slotProps={{ paper: { className: 'mt-1.5 rounded-xl' } }}
              >
                <div className="w-[220px] p-1">
                  <div className="text-muted-foreground px-2.5 py-1.5 text-[11px] font-medium tracking-[0.08em] uppercase">
                    Brands
                  </div>
                  <ul className="max-h-64 space-y-0.5 overflow-y-auto py-0.5">
                    {brands.map((brand) => (
                      <li key={brand.id}>
                        <label className="hover:bg-accent flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5">
                          <input
                            type="checkbox"
                            checked={brandIds.includes(brand.id)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const newBrandIds = checked
                                ? [...brandIds, brand.id]
                                : brandIds.filter((id) => id !== brand.id);
                              onFilterChange('brandIds', newBrandIds);
                            }}
                            className="border-input accent-foreground size-3.5 shrink-0 rounded-sm border"
                          />
                          <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
                            {capitalCase(brand.name)}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  {filtersNumber > 0 && (
                    <div className="border-border/60 mt-0.5 border-t pt-0.5">
                      <button
                        type="button"
                        className="text-muted-foreground hover:bg-accent hover:text-foreground w-full rounded-md px-2.5 py-1.5 text-left text-sm transition-colors"
                        onClick={() => {
                          clearFilter();
                          handleClose();
                        }}
                      >
                        Clear filters
                      </button>
                    </div>
                  )}
                </div>
              </Dropdown>
            </>
          )}
        </div>
      </div>
      <div className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="w-full overflow-x-auto px-3 pt-2 pb-1">
          <table className="w-full min-w-[640px] table-fixed border-collapse text-left">
            <colgroup>
              <col style={{ width: '25%' }} />
              {showBrandColumn ? <col style={{ width: '25%' }} /> : null}
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '48px' }} />
            </colgroup>
            <thead>
              <tr className="text-muted-foreground text-[13px]">
                <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Report</th>
                {showBrandColumn ? (
                  <SortableTableHeader
                    label="Brand"
                    order={brandSortOrder}
                    onClick={toggleBrandSort}
                    thClassName="w-1/4"
                  />
                ) : null}
                <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Unresolved</th>
                <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Last scan</th>
                <th className="px-2 pt-1 pb-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-border/50 border-t" aria-hidden>
                    <td className="px-2 py-3.5">
                      <Skeleton className="h-4 w-40" />
                    </td>
                    {showBrandColumn ? (
                      <td className="px-2 py-3.5">
                        <Skeleton className="h-4 w-16" />
                      </td>
                    ) : null}
                    <td className="px-2 py-3.5">
                      <Skeleton className="h-5 w-8" />
                    </td>
                    <td className="px-2 py-3.5">
                      <Skeleton className="h-4 w-20" />
                    </td>
                    <td className="px-2 py-3.5">
                      <Skeleton className="ml-auto size-6" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr className="border-border/50 border-t">
                  <td
                    colSpan={showBrandColumn ? 5 : 4}
                    className="text-muted-foreground px-2 py-8 text-center text-sm"
                  >
                    No scan results yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                    const unresolved =
                      row.policyFeedbackCount + row.claimCount + row.trademarkCount;
                    const brandLabel =
                      row.brandName && row.brandName !== row.brandId ? row.brandName : '—';
                    const isActive = queryParamReportId === row.id;

                    return (
                      <tr
                        key={row.id}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open report ${row.name}`}
                        aria-current={isActive ? 'true' : undefined}
                        className={cn(
                          'border-border/50 hover:bg-muted focus-visible:bg-muted cursor-pointer border-t transition-colors focus-visible:outline-none',
                          isActive && 'bg-muted/70',
                        )}
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button') || target.closest('[role="menuitem"]')) {
                            return;
                          }
                          openReport(row.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openReport(row.id);
                          }
                        }}
                      >
                        <td className="overflow-hidden px-2 py-3.5 align-middle">
                          <div className="text-foreground inline-flex max-w-full items-center gap-1.5 text-sm font-medium">
                            <span className="truncate">{row.name}</span>
                            {row.isReviewed && (
                              <DocumentCheckIcon
                                height={14}
                                strokeWidth={2}
                                className="text-muted-foreground shrink-0"
                                aria-label="Reviewed"
                              />
                            )}
                          </div>
                        </td>
                        {showBrandColumn ? (
                          <td className="overflow-hidden px-2 py-3.5 align-middle">
                            <span className="text-muted-foreground block truncate text-sm">
                              {brandLabel}
                            </span>
                          </td>
                        ) : null}
                        <td className="overflow-hidden px-2 py-3.5 align-middle">
                          <Chip
                            level={unresolved > 0 ? 'alert' : 'success'}
                            text={unresolved}
                            className="px-2 py-0.5 text-xs tabular-nums"
                          />
                        </td>
                        <td className="overflow-hidden px-2 py-3.5 align-middle">
                          <span className="text-muted-foreground block truncate text-sm">
                            {row.lastScanAt || '—'}
                          </span>
                        </td>
                        <td
                          className="overflow-hidden px-1 py-3.5 align-middle"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-end">
                            <ReportActions
                              reportId={row.id}
                              isReviewed={row.isReviewed}
                              isPendingDelete={pendingDeleteIds.has(row.id)}
                              onPendingDelete={handlePendingDelete}
                              onUndoDelete={handleUndoDelete}
                              onDeleteSettled={handleDeleteCommitted}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
        {total !== reports.length && total > 1 && (
          <div
            className="border-border/60 border-t px-3 py-2"
            onMouseEnter={prefetchNextPage}
            onFocusCapture={prefetchNextPage}
          >
            <Pagination
              dense
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              total={Math.ceil(total / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              totalItems={total}
              itemLabel="reports"
              rangeOnly
            />
            {isFetching && !isLoading ? (
              <span className="text-primary mt-1 flex justify-end" aria-label="Loading reports">
                <AppSpinner size="xs" />
              </span>
            ) : null}
          </div>
        )}
      </div>
      {queryParamReportId ? (
        <Report
          reportId={queryParamReportId}
          brandId={currentReport?.brandId || UNKNOWN_BRAND_ID}
          onClose={() => updateSearchParams('reportId', null)}
          onPendingDelete={handlePendingDelete}
          onUndoDelete={handleUndoDelete}
          onDeleteSettled={handleDeleteCommitted}
        />
      ) : null}
    </section>
  );
}
